import React, { useCallback } from 'react';
import { WithStylesProps } from 'react-jss';

import { EM_DASH } from '~/common/constants/unicode';
import EcDatepicker from '~/components/Shared/EcDatepicker/EcDatepicker';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { parseDate } from '~/eds/src/utils/parseDate';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { parseUserDate } from '~/i18n/utils';

import styles from '../InformationField.styles';
import { Field } from '../InformationFieldV2';

interface DateFieldProps extends WithStylesProps<typeof styles> {
  field: Field;
  editMode: boolean;
  fieldValue: any;
  setFieldValue: (value: any) => void;
}

const DateFieldComponent = ({
  classes,
  field,
  editMode,
  fieldValue,
  setFieldValue,
}: DateFieldProps) => {
  const formatDate = useFormatDate();
  const enableLocalization = useFlag(FlagType.LanguageSelect);

  const handleLocalizedDateChange = useCallback(
    (date: Date | null) => {
      const newDate = date ? formatDate(date, 'iso_date') : null;
      setFieldValue({ display_value: newDate, value: newDate });
    },
    [formatDate, setFieldValue],
  );

  const handleLegacyDateChange = useCallback(
    (date: Date | null) => {
      const newDate = date ? formatDate(date, 'iso') : null;
      setFieldValue({ display_value: newDate, value: newDate });
    },
    [formatDate, setFieldValue],
  );

  const renderDisplayMode = () => {
    return !fieldValue || fieldValue.display_value === ''
      ? EM_DASH
      : formatDate(parseDate(fieldValue.display_value, true), 'long');
  };

  const renderLocalizedDateInput = () => {
    const pdate = fieldValue ? fieldValue.value : '';
    const parsedDateValue = pdate ? parseUserDate(pdate) : null;

    return (
      <div className={classes.editItemContainer}>
        <DateInput
          name={field.name}
          value={parsedDateValue}
          disabled={!field.is_editable}
          onChange={handleLocalizedDateChange}
        />
      </div>
    );
  };

  const renderLegacyDateInput = () => {
    const pdate = fieldValue ? fieldValue.value : '';

    return (
      <div className={classes.editItemContainer}>
        <EcDatepicker
          title={field.name}
          date={pdate ? parseDate(pdate, true) : null}
          onDateChange={handleLegacyDateChange}
          isClearable={field.is_nullable}
          disabled={!field.is_editable}
        />
      </div>
    );
  };

  const renderEditMode = () => {
    return enableLocalization
      ? renderLocalizedDateInput()
      : renderLegacyDateInput();
  };

  if (editMode) {
    return renderEditMode();
  }
  return renderDisplayMode();
};

export const DateField = React.memo(DateFieldComponent);
